import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  useClearFixtureStatusOverride,
  useGateMarket,
  useOptaLiveDashboard,
  useSetFixtureLifecycleStatus,
} from '../api/hooks';
import { fetchOptaLiveExport } from '../api';
import {
  effectiveFixtureStatus,
  formatApiError,
  LifecycleButtons,
  statusBadge,
  WriterFanoutBanner,
} from './liveFixtureLifecycleUi';
import {
  liveFeedLabel,
  liveFeedSource,
  liveVendorId,
  secondLatchLabel,
} from './liveFeedUi';
import {
  applyEventFilters,
  compareEventsChronologyDesc,
  compareMarketsOfferedFirst,
  dedupeEventsByFixtureRow,
  distinctTypeLabels,
  eventBusinessKey,
  eventDescription,
  eventOffense,
  eventTypeLabel,
  formatDownDistance,
  formatEventScore,
  formatFieldPosition,
  formatFootballClock,
  formatPlayYards,
  hasAmericanFootballEvents,
  isDangerZone,
  isSuspensionTrigger,
  supportsPlayerFilter,
} from './liveDashboardFilters';
import { useHomebaseModal } from '../components/HomebaseModal';
import ProbabilityMeter from '../components/ProbabilityMeter';
import './ActiveFixturesPage.css';
import './LivePage.css';
import './MarketsPage.css';

function formatTs(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return String(iso);
  }
}

/** When the row was persisted (``opta_match_events.received_at_utc``), not match clock. */
function formatReceivedAt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return String(iso);
  }
}

function fmtPct(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—';
  return `${(Number(x) * 100).toFixed(1)}%`;
}

// Residual is derived, not stored: line − recorded.
function fmtRecorded(m) {
  const state = m?.recorded_state;
  if (!state) return '—';
  if (state !== 'known') return state;
  const v = Number(m.recorded_value);
  if (!Number.isFinite(v)) return state;
  const shown = Number.isInteger(v) ? String(v) : v.toFixed(1);
  const line = Number(m.line);
  if (!Number.isFinite(line)) return shown;
  const residual = line - v;
  return `${shown} → ${Number.isInteger(residual) ? residual : residual.toFixed(1)}`;
}

function fmtKalshiTs(ts) {
  if (ts == null || !Number.isFinite(Number(ts))) return '—';
  try {
    return new Date(Number(ts) * 1000).toLocaleTimeString();
  } catch {
    return '—';
  }
}

/**
 * Legs a fixture's Kalshi map can carry, in display order. Soccer has a draw; football has
 * no draw but adds one spread rung, which is the leg that stays responsive once the
 * moneyline flattens in a blowout — so it has to be visible, not folded away.
 */
const KALSHI_LEGS = [
  { key: 'home', label: 'H', name: 'Home moneyline' },
  { key: 'away', label: 'A', name: 'Away moneyline' },
  { key: 'draw', label: 'D', name: 'Draw' },
  { key: 'spread_yes', label: 'SPR', name: 'Spread rung' },
];

function KalshiLivePanel({ kalshi }) {
  const prices = kalshi?.prices || {};
  const tickers = kalshi?.tickers || {};
  const paused = Boolean(kalshi?.paused);
  const configured = Boolean(kalshi?.configured);
  const enabled = Boolean(kalshi?.enabled);
  // Only the legs this fixture actually has: an empty draw slot made the football panel
  // look like it was missing data, and an absent spread rung is a real coverage gap rather
  // than a blank to render.
  const legs = KALSHI_LEGS.filter(
    (leg) => tickers[leg.key] || prices[leg.key] != null
  );
  const hasDraw = Boolean(tickers.draw || prices.draw != null);
  const hasSpread = Boolean(tickers.spread_yes || prices.spread_yes != null);
  // ``enabled`` comes from PG (live pod stream), not the dashboard API's kalshi_live_flags YAML.
  const status = !configured
    ? 'unmapped'
    : !enabled
      ? 'waiting'
      : paused
        ? 'PAUSED'
        : 'open';
  const sourceLabel =
    kalshi?.mapping_source === 'optic'
      ? 'auto-mapped from Optic'
      : kalshi?.mapping_source === 'manual'
        ? 'manual mapping'
        : null;
  // Everything explanatory rides one tooltip so the panel stays a single line.
  const note = [
    paused
      ? `Pause flag active${kalshi?.reason ? `: ${kalshi.reason}` : ''}.`
      : hasDraw
        ? 'Pauses on market_paused; unpauses when Kalshi quotes return valid.'
        : 'Closes temporarily on a move that is large relative to the market\u2019s own recent volatility, or on a Kalshi pause.',
    !hasDraw && !hasSpread
      ? 'Moneyline only \u2014 no spread rung mapped, so the signal flattens in a blowout.'
      : null,
    sourceLabel,
    kalshi?.last_update_ts ? `Last tick ${fmtKalshiTs(kalshi.last_update_ts)}.` : null,
    kalshi?.mapping_error,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`live-kalshi-panel${paused ? ' live-kalshi-panel--paused' : ''}`}>
      <span
        className="live-panel-label"
        title={`Kalshi ${hasDraw ? 'three' : 'two'}-way win probability${
          hasSpread ? ' + spread rung' : ''
        }`}
      >
        Kalshi
      </span>
      <span
        className={`live-kalshi-status${paused ? ' live-kalshi-status--paused' : ''}`}
        title={note}
      >
        {status}
      </span>
      <span className="live-kalshi-wp-inline">
        {legs.map((leg) => (
          <span
            className="live-kalshi-wp"
            key={leg.key}
            title={`${leg.name}: ${tickers[leg.key] || 'no ticker'}`}
          >
            <span className="live-kalshi-role">{leg.label}</span>
            <strong>{fmtPct(prices[leg.key])}</strong>
          </span>
        ))}
      </span>
      {kalshi?.mapping_error && (
        <span className="live-kalshi-alert" title={kalshi.mapping_error}>
          !
        </span>
      )}
    </div>
  );
}

function fmtCi(lo, hi) {
  const a = lo != null ? Number(lo) : NaN;
  const b = hi != null ? Number(hi) : NaN;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return '—';
  return `${(a * 100).toFixed(1)}–${(b * 100).toFixed(1)}%`;
}

function fmtPx(x) {
  if (x == null || !Number.isFinite(Number(x))) return '—';
  return Number(x).toFixed(2);
}

function fmtOddsPair(row) {
  const o = fmtPx(row.marginated_price);
  const uRaw = row.marginated_price_under;
  const uNum = uRaw != null ? Number(uRaw) : NaN;
  const u = Number.isFinite(uNum) && uNum > 0 ? uNum.toFixed(2) : null;
  if (u) return `${o} / ${u}`;
  return o;
}

/**
 * Event-type filter as a dropdown rather than a chip row.
 *
 * A football fixture offers a dozen-odd types (rush / pass / punt / kickoff / field_goal /
 * penalty / timeout / period_end …); rendered inline they either wrapped into a band taller
 * than the table or scrolled sideways under the cursor. As a popover the trigger stays one
 * button wide and the list scrolls on its own.
 */
function EventTypeFilter({ options, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Click-away and Escape: a popover that can only be closed by its own button is a trap
  // when the thing behind it is a table you want to read.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!options || options.length === 0) return null;
  const count = selected.length;

  return (
    <div className="live-type-filter" ref={wrapRef}>
      <button
        type="button"
        className={`live-type-filter-btn${count > 0 ? ' live-type-filter-btn--on' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="Filter by event type"
      >
        {count > 0 ? `Types · ${count}` : 'Types'}
        <span className="live-type-filter-caret" aria-hidden>
          {open ? '\u25B2' : '\u25BC'}
        </span>
      </button>
      {count > 0 && (
        <button
          type="button"
          className="live-events-clear-types"
          onClick={onClear}
          title="Clear the type filter"
        >
          Clear
        </button>
      )}
      {open && (
        <div className="live-type-filter-menu">
          <div className="live-type-filter-list">
            {options.map((name) => (
              <label key={name} className="live-type-filter-item">
                <input
                  type="checkbox"
                  checked={selected.includes(name)}
                  onChange={() => onToggle(name)}
                />
                <span>{name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function eventRowClass(ev) {
  if (isSuspensionTrigger(ev)) return 'live-event-trigger';
  if (isDangerZone(ev)) return 'live-event-danger';
  return '';
}

function formatBlockTags(tags) {
  if (!tags) return '—';
  if (Array.isArray(tags)) return tags.join(', ') || '—';
  return String(tags);
}

function formatMatchClock(ev) {
  if (ev.match_clock) return ev.match_clock;
  if (ev.time_min != null) {
    const sec =
      ev.time_sec != null ? `:${String(ev.time_sec).padStart(2, '0')}` : '';
    return `${ev.time_min}${sec}`;
  }
  return '—';
}

export default function LiveDashboardPage() {
  const { fixtureId } = useParams();
  const location = useLocation();
  const optaIdFromNav = location.state?.optaId;
  const { data, isLoading, error, refetch, dataUpdatedAt } = useOptaLiveDashboard(
    fixtureId,
    optaIdFromNav
  );
  const gateMarket = useGateMarket();
  const { confirm, ModalLayer } = useHomebaseModal();

  const runGate = useCallback(
    async (marketKey, payload) => {
      if (!marketKey) return;
      const ok = await confirm({
        title: payload.force_two_sided
          ? 'Two-sided shaping'
          : payload.force_one_sided
            ? 'One-sided shaping'
            : 'Suspend quoting',
        body: payload.force_two_sided
          ? 'Apply two-sided shaping for this market?'
          : payload.force_one_sided
            ? 'Apply one-sided (over-only) shaping for this market?'
            : 'Suspend quoting for this market?',
        danger: !payload.force_two_sided && !payload.force_one_sided,
        confirmLabel: payload.force_two_sided || payload.force_one_sided ? 'Apply' : 'Suspend',
      });
      if (!ok) return;
      await gateMarket.mutateAsync({
        marketKey,
        gatedBy: 'live-dashboard',
        ...payload,
      });
    },
    [gateMarket, confirm]
  );

  const fixture = data?.fixture || {};
  const gameId = fixture.game_id || fixtureId;
  const hasOverride = Boolean(fixture.status_override);
  const effStatus = effectiveFixtureStatus(fixture);
  const block = data?.block_state;
  const kalshiLive = data?.kalshi_live || {};
  const eventsScrollRef = useRef(null);
  const prevFixtureIdRef = useRef(fixtureId);
  const [playerEventsOnly, setPlayerEventsOnly] = useState(false);
  const [blockedRowsOnly, setBlockedRowsOnly] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [fanoutWriters, setFanoutWriters] = useState(true);
  const [marketQuery, setMarketQuery] = useState('');
  const [marketView, setMarketView] = useState('all');
  const [lifecycleError, setLifecycleError] = useState(null);
  const [lifecycleResult, setLifecycleResult] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState(null);

  const setStatusMut = useSetFixtureLifecycleStatus();
  const clearOverrideMut = useClearFixtureStatusOverride();
  const lifecycleBusy = setStatusMut.isPending || clearOverrideMut.isPending;

  const eventTotal = data?.event_total ?? data?.events?.length ?? 0;
  const eventsTruncated = Boolean(data?.events_truncated);

  const allEvents = useMemo(() => {
    const rows = dedupeEventsByFixtureRow(data?.events || []);
    rows.sort(compareEventsChronologyDesc);
    return rows;
  }, [data?.events]);

  const filterKey = `${playerEventsOnly ? 'p' : ''}${blockedRowsOnly ? 'b' : ''}-${[...selectedTypes].sort().join('|')}`;

  const typeOptions = useMemo(() => distinctTypeLabels(allEvents), [allEvents]);
  // Football rows carry nothing in the Opta columns (x/y null, outcome = end down), so the
  // table swaps to down-and-distance columns read from ``qualifiers``.
  const footballEvents = useMemo(() => hasAmericanFootballEvents(allEvents), [allEvents]);
  const playerFilterSupported = useMemo(() => supportsPlayerFilter(allEvents), [allEvents]);

  const filteredEvents = useMemo(
    () =>
      applyEventFilters(allEvents, {
        playerEventsOnly,
        selectedTypes,
        blockedRowsOnly,
      }),
    [allEvents, playerEventsOnly, selectedTypes, blockedRowsOnly]
  );

  const toggleType = (name) => {
    setSelectedTypes((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]
    );
  };

  useEffect(() => {
    if (prevFixtureIdRef.current === fixtureId) return;
    prevFixtureIdRef.current = fixtureId;
    setSelectedTypes([]);
    setPlayerEventsOnly(false);
    setBlockedRowsOnly(false);
    setLifecycleError(null);
    setLifecycleResult(null);
    setMarketQuery('');
    setMarketView('all');
    const el = eventsScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [fixtureId]);

  const applyLifecycle = async (status) => {
    if (!status || !gameId) return;
    setLifecycleError(null);
    setLifecycleResult(null);
    try {
      const res = await setStatusMut.mutateAsync({
        gameId,
        status,
        reason: 'live_fixture_dashboard',
        fanout_writers: fanoutWriters,
      });
      setLifecycleResult(res);
      await refetch();
    } catch (e) {
      setLifecycleError(formatApiError(e));
    }
  };

  const clearLifecycleOverride = async () => {
    setLifecycleError(null);
    setLifecycleResult(null);
    try {
      const res = await clearOverrideMut.mutateAsync({ gameId, fanoutWriters });
      setLifecycleResult(res);
      await refetch();
    } catch (e) {
      setLifecycleError(formatApiError(e));
    }
  };

  const downloadLiveLogs = async () => {
    if (!gameId) return;
    setExportBusy(true);
    setExportError(null);
    try {
      const payload = await fetchOptaLiveExport(gameId, {
        opta_id: fixture.opta_id || undefined,
      });
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `live-export-${gameId}-${stamp}.json`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(formatApiError(e));
    } finally {
      setExportBusy(false);
    }
  };

  const markets = useMemo(() => {
    const rows = [...(data?.markets || [])];
    rows.sort(compareMarketsOfferedFirst);
    return rows;
  }, [data?.markets]);

  /** Headline counts so "what is actually offered right now" is visible without reading rows. */
  const marketSummary = useMemo(() => {
    const out = {
      total: markets.length,
      open: 0,
      suspended: 0,
      gated: 0,
      twoSided: 0,
      oneSided: 0,
      players: new Set(),
      types: new Set(),
    };
    for (const m of markets) {
      if (m.manual_suspend) out.suspended += 1;
      else if (!m.gate_allowed) out.gated += 1;
      else out.open += 1;
      if (m.quote_two_sided) out.twoSided += 1;
      else out.oneSided += 1;
      if (m.player_name) out.players.add(m.player_name);
      if (m.market_id) out.types.add(m.market_id);
    }
    return {
      ...out,
      playerCount: out.players.size,
      typeCount: out.types.size,
    };
  }, [markets]);

  const visibleMarkets = useMemo(() => {
    const q = marketQuery.trim().toLowerCase();
    return markets.filter((m) => {
      if (marketView === 'open' && !(m.gate_allowed && !m.manual_suspend)) return false;
      if (marketView === 'closed' && m.gate_allowed && !m.manual_suspend) return false;
      if (!q) return true;
      return (
        String(m.player_name || '').toLowerCase().includes(q) ||
        String(m.market_id || '').toLowerCase().includes(q) ||
        String(m.market_key || '').toLowerCase().includes(q)
      );
    });
  }, [markets, marketQuery, marketView]);
  const blocked = Boolean(block?.blocked);
  const feedSource = liveFeedSource(block, fixture);
  const isSportRadar = feedSource === 'sportradar';
  const feedLabel = liveFeedLabel(feedSource);
  // The shared second-latch column means something different per feed: Opta's danger zone,
  // SportRadar's offence close, Genius's "a risk is live right now".
  const latchLabel = secondLatchLabel(feedSource);
  const latchDetail =
    isSportRadar && block?.offense_block_side ? ` (${block.offense_block_side})` : '';
  const vendorId = liveVendorId(fixture, block);
  const title = [fixture.home, fixture.away].filter(Boolean).join(' vs ') || fixtureId;

  if (isLoading && !data) {
    return (
      <div className="live-page active-fixtures-page">
        <div className="loading">Loading live dashboard…</div>
      </div>
    );
  }

  return (
    <div className="live-page active-fixtures-page">
      <div className="live-breadcrumb">
        <Link to="/game/live">Live</Link>
        <span> / </span>
        <span>{title}</span>
      </div>

      <div className="live-compact-header">
        <div className="live-compact-title">
          <h1>{title}</h1>
          <span className="live-compact-ids" title={`${feedLabel} feed id · Optic fixture id`}>
            {feedLabel} {vendorId || '—'} · Optic {gameId}
          </span>
        </div>
        <div className="live-compact-actions">
          {dataUpdatedAt > 0 && (
            <span className="live-compact-updated">
              {new Date(dataUpdatedAt).toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            className="button"
            onClick={() => refetch()}
            disabled={lifecycleBusy || exportBusy}
          >
            Refresh
          </button>
          <button
            type="button"
            className="button button-secondary"
            onClick={downloadLiveLogs}
            disabled={!gameId || exportBusy || lifecycleBusy}
            title="Download JSON: live events, block state, Kalshi history, feed snapshot, fixture row"
          >
            {exportBusy ? 'Exporting…' : 'Logs'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          Failed to load dashboard: {error.message || 'unknown error'}
        </div>
      )}
      {lifecycleError && <div className="error-message">{lifecycleError}</div>}
      {exportError && <div className="error-message">{exportError}</div>}
      <WriterFanoutBanner result={lifecycleResult} />

      <div className="live-signal-strip">
      <div
        className={`live-game-status-bar${
          effStatus === 'suspended' ? ' live-game-status-bar--suspended' : ''
        }${effStatus === 'live' ? ' live-game-status-bar--live' : ''}${
          effStatus === 'complete' ? ' live-game-status-bar--complete' : ''
        }`}
      >
        {statusBadge(fixture)}
        {hasOverride && (
          <span
            className="live-override-tag"
            title={fixture.status_override_reason || 'Manual lifecycle lock'}
          >
            lock
          </span>
        )}
        {!hasOverride && fixture.status && fixture.status !== effStatus && (
          <span className="live-muted live-game-status-db" title="Status stored in the DB">
            db:{fixture.status}
          </span>
        )}
        <LifecycleButtons
          current={effStatus}
          busy={lifecycleBusy}
          hasOverride={hasOverride}
          onSelect={applyLifecycle}
          onClearOverride={clearLifecycleOverride}
          size="sm"
        />
        <label
          className="live-kill-fanout-check live-sync-toggle"
          title="Sync the sport writers after a lifecycle change"
        >
          <input
            type="checkbox"
            checked={fanoutWriters}
            onChange={(e) => setFanoutWriters(e.target.checked)}
            disabled={lifecycleBusy}
          />
          <span>sync</span>
        </label>
      </div>

      <div className={`live-block-banner ${blocked ? 'blocked' : 'clear'}`}>
        <span className="live-panel-label">{feedLabel}</span>
        <strong className="live-block-state">{blocked ? 'BLOCKED' : 'open'}</strong>
        {block?.suspension_countdown_remaining_s > 0 && (
          <span className="live-block-countdown" title="Reopens when the hold elapses">
            {block.suspension_countdown_remaining_s}s
          </span>
        )}
        {block?.blocked_reason && (
          <span className="live-block-reason" title={block.blocked_reason}>
            {block.blocked_reason}
          </span>
        )}
        <span className="live-latch-dots">
          <span
            className={`live-latch-dot ${block?.suspension_latched ? 'on' : 'off'}`}
            title={`Suspension latch: ${block?.suspension_latched ? 'ON' : 'off'}`}
          >
            S
          </span>
          <span
            className={`live-latch-dot ${block?.danger_zone_latched ? 'on' : 'off'}`}
            title={`${latchLabel}: ${
              block?.danger_zone_latched ? `ON${latchDetail}` : 'off'
            }`}
          >
            {isSportRadar
              ? (block?.offense_block_side || 'O').charAt(0).toUpperCase()
              : latchLabel.charAt(0).toUpperCase()}
          </span>
        </span>
        {block?.last_opta_activity_at && (
          <span
            className="live-block-meta"
            title={`Last feed activity: ${formatTs(block.last_opta_activity_at)}`}
          >
            {formatTs(block.last_opta_activity_at)}
          </span>
        )}
      </div>

      <KalshiLivePanel kalshi={kalshiLive} />
      </div>

      <div className="live-body-grid">
      <div className="card live-section live-markets-card">
        <div
          className="live-markets-head"
          title="Same rows and actions as Markets · Summary (7-day window). Sus / 2w / 1w call the writer gate API."
        >
          <h2>Markets</h2>
        </div>

        <div className="live-market-bar">
          <div className="live-market-chips">
            <button
              type="button"
              className={`live-chip${marketView === 'all' ? ' live-chip--on' : ''}`}
              onClick={() => setMarketView('all')}
              title="Show every market on this fixture"
            >
              <strong>{marketSummary.total}</strong> available
            </button>
            <button
              type="button"
              className={`live-chip live-chip--open${
                marketView === 'open' ? ' live-chip--on' : ''
              }`}
              onClick={() => setMarketView(marketView === 'open' ? 'all' : 'open')}
              title="Quoting now (gate allowed, not manually suspended)"
            >
              <strong>{marketSummary.open}</strong> quoting
            </button>
            <button
              type="button"
              className={`live-chip live-chip--closed${
                marketView === 'closed' ? ' live-chip--on' : ''
              }`}
              onClick={() => setMarketView(marketView === 'closed' ? 'all' : 'closed')}
              title={`Closed: ${marketSummary.suspended} manually suspended, ${marketSummary.gated} gated by the engine or live feed`}
            >
              <strong>{marketSummary.suspended + marketSummary.gated}</strong> closed
            </button>
            <span
              className="live-chip live-chip--static"
              title="Shape of the published quotes: two-sided vs over-only"
            >
              <strong>
                {marketSummary.twoSided}/{marketSummary.oneSided}
              </strong>{' '}
              2w/1w
            </span>
            <span
              className="live-chip live-chip--static"
              title={`${marketSummary.playerCount} players across ${marketSummary.typeCount} market types`}
            >
              <strong>{marketSummary.playerCount}</strong> players
            </span>
          </div>
          {markets.length > 0 && (
            <div className="live-market-filter">
              <input
                type="search"
                className="live-market-search"
                placeholder="Filter player / market / key…"
                value={marketQuery}
                onChange={(e) => setMarketQuery(e.target.value)}
              />
              <span className="live-muted live-market-filter-count">
                {visibleMarkets.length}/{markets.length}
              </span>
              {(marketQuery || marketView !== 'all') && (
                <button
                  type="button"
                  className="live-events-clear-types"
                  onClick={() => {
                    setMarketQuery('');
                    setMarketView('all');
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {markets.length === 0 ? (
          <p className="no-data">No quoted markets for this fixture.</p>
        ) : visibleMarkets.length === 0 ? (
          <p className="no-data">No markets match the current filter.</p>
        ) : (
          <div className="markets-table-wrap live-markets-scroll">
            <table className="table markets-table">
              <thead>
                <tr>
                  <th className="live-mkt-col-name">Player · market · line</th>
                  <th className="live-mkt-col-quote" title="Published decimal odds">
                    Quote (over / under)
                  </th>
                  <th
                    className="live-mkt-col-fair"
                    title="Fair probability of the over, with its 90% interval. The meter shows where that probability sits on 0-100% (tick) and how wide the interval is (band)."
                  >
                    Fair p̂ · 90% CI
                  </th>
                  <th title="In-game accumulated stat. Counts price on the residual (line − recorded); yardage stays absolute.">
                    Rec
                  </th>
                  <th title="Book count behind the quote">Bks</th>
                  <th title="quote_two_sided: two-sided vs over-only">Shape</th>
                  <th title="Quote allowed vs suspended">Status</th>
                  <th title="Σ entry fees across all lines in the window">Σ fee</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleMarkets.map((m) => {
                  const mk = m.market_key;
                  const suspended = Boolean(m.manual_suspend);
                  const gated = !m.gate_allowed;
                  const shape =
                    m.quote_shape_label || (m.quote_two_sided ? '2w' : '1w');
                  const shapeTitle =
                    m.quotes_both_over_under === true
                      ? 'Two-sided quote with over and under marginated prices.'
                      : m.quote_two_sided
                        ? 'Two-sided shape; under decimal missing or zero.'
                        : 'One-sided (over-only) quote shape.';
                  return (
                    <tr
                      key={mk}
                      className={
                        suspended || gated ? 'live-mkt-row live-mkt-row--closed' : 'live-mkt-row'
                      }
                    >
                      <td className="live-mkt-name">
                        <span className="live-mkt-player">{m.player_name || '—'}</span>
                        <span className="live-mkt-meta">
                          {m.market_id || '—'} · line {m.line != null ? m.line : '—'}
                        </span>
                      </td>
                      <td className="live-mkt-quote">
                        <span className="live-mkt-odds">{fmtOddsPair(m)}</span>
                      </td>
                      <td className="live-mkt-fair">
                        <div className="live-mkt-fair-nums">
                          <span className="live-mkt-fair-p">{fmtPct(m.p_fair)}</span>
                          <span className="live-mkt-fair-ci">
                            {fmtCi(m.p_lower, m.p_upper)}
                          </span>
                        </div>
                        <ProbabilityMeter
                          pFair={m.p_fair}
                          pLower={m.p_lower}
                          pUpper={m.p_upper}
                          label={`${m.player_name || 'market'} ${m.market_id || ''} ${
                            m.line != null ? m.line : ''
                          }`}
                        />
                      </td>
                      <td className="live-mkt-rec">{fmtRecorded(m)}</td>
                      <td>{m.n_books != null ? m.n_books : '—'}</td>
                      <td className="mk-muted" title={shapeTitle}>
                        {shape}
                      </td>
                      <td>
                        {suspended ? (
                          <span
                            className="markets-badge markets-badge--sus"
                            title={m.manual_gate_reason || 'Manually suspended'}
                          >
                            Suspended
                          </span>
                        ) : gated ? (
                          <span
                            className="markets-badge markets-badge--gate"
                            title={m.gate_reason || 'Gated by the engine or live feed'}
                          >
                            Closed
                          </span>
                        ) : (
                          <span className="markets-badge markets-badge--open">Quoting</span>
                        )}
                      </td>
                      <td className="mk-muted">{fmtPx(m.fees_window)}</td>
                      <td>
                        <div className="markets-actions">
                          <button
                            type="button"
                            disabled={gateMarket.isPending}
                            onClick={() => runGate(mk, {})}
                          >
                            Sus
                          </button>
                          <button
                            type="button"
                            disabled={gateMarket.isPending}
                            onClick={() => runGate(mk, { force_two_sided: true })}
                          >
                            2w
                          </button>
                          <button
                            type="button"
                            disabled={gateMarket.isPending}
                            onClick={() => runGate(mk, { force_one_sided: true })}
                          >
                            1w
                          </button>
                          <Link
                            className="markets-link"
                            to={`/dashboard?market=${encodeURIComponent(mk)}`}
                          >
                            →
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card live-section">
        <h2>
          {feedLabel} events ({filteredEvents.length}
          {filteredEvents.length !== allEvents.length ? ` / ${allEvents.length}` : ''}
          {eventTotal > allEvents.length ? ` · ${allEvents.length} loaded of ${eventTotal}` : ''})
        </h2>

        {eventsTruncated && (
          <p className="live-events-truncation-warning">
            Showing latest {allEvents.length} of {eventTotal} persisted events (early match
            events omitted).
          </p>
        )}
        {allEvents.length > 0 && (
          <div className="live-events-filter-bar--compact">
            {playerFilterSupported && (
              <label
                className="live-events-filter-check"
                title="Only rows whose player name is set"
              >
                <input
                  type="checkbox"
                  checked={playerEventsOnly}
                  onChange={(e) => setPlayerEventsOnly(e.target.checked)}
                />
                <span>Players</span>
              </label>
            )}
            <label
              className="live-events-filter-check"
              title={
                footballEvents
                  ? 'Only rows that fired a close rule (block_tags set)'
                  : 'Only rows that contributed to a block (red = trigger, yellow = danger zone)'
              }
            >
              <input
                type="checkbox"
                checked={blockedRowsOnly}
                onChange={(e) => setBlockedRowsOnly(e.target.checked)}
              />
              <span>Blocking</span>
            </label>
            <EventTypeFilter
              options={typeOptions}
              selected={selectedTypes}
              onToggle={toggleType}
              onClear={() => setSelectedTypes([])}
            />
            <span className="live-muted live-events-filter-status">
              {filteredEvents.length}/{allEvents.length}
            </span>
          </div>
        )}
        {allEvents.length === 0 ? (
          <p className="no-data">No persisted events yet (WS or REST backfill).</p>
        ) : filteredEvents.length === 0 ? (
          <p className="no-data">No events match the current filters.</p>
        ) : (
          <div
            ref={eventsScrollRef}
            className="markets-table-wrap live-events-scroll"
          >
            <table
              className={`table markets-table live-events-table${
                footballEvents ? ' live-events-table--football' : ''
              }`}
            >
              <thead>
                <tr>
                  {/* `table-layout: fixed` resolves every column width from the FIRST row
                      alone, so these two carry the width classes — matching them only on the
                      body cells left them as auto columns splitting the leftover space with
                      Detail. */}
                  <th className={footballEvents ? 'live-ev-clk' : undefined}>Clk</th>
                  <th className={footballEvents ? 'live-ev-type' : undefined}>Type</th>
                  {footballEvents ? (
                    <>
                      <th className="live-ev-off">Off</th>
                      <th className="live-ev-down">Down</th>
                      <th className="live-ev-ball">Ball on</th>
                      <th className="live-ev-yds">Yds</th>
                      <th className="live-ev-score">Score</th>
                      <th className="live-events-desc-col">Detail</th>
                    </>
                  ) : (
                    <>
                      <th>Player</th>
                      <th>x</th>
                      <th>y</th>
                      <th>Out</th>
                    </>
                  )}
                  <th className="live-events-tags">Tags</th>
                  <th className="live-events-received-col" title="opta_match_events.received_at_utc">
                    Received
                  </th>
                  {/* Always "push" on a SportRadar feed; the column only earns its width on
                      soccer, where it separates WS from REST backfill. */}
                  {!footballEvents && <th className="live-events-src-col">Src</th>}
                </tr>
              </thead>
              <tbody key={filterKey}>
                {filteredEvents.map((ev) => (
                  <tr
                    key={eventBusinessKey(ev) || `row-${ev.id}`}
                    className={eventRowClass(ev)}
                  >
                    <td
                      className={footballEvents ? 'live-ev-clk' : undefined}
                      title={footballEvents ? formatFootballClock(ev) : undefined}
                    >
                      {footballEvents ? formatFootballClock(ev) : formatMatchClock(ev)}
                    </td>
                    <td
                      className={footballEvents ? 'live-ev-type' : undefined}
                      title={footballEvents ? eventTypeLabel(ev) : undefined}
                    >
                      {eventTypeLabel(ev) || '—'}
                      {!footballEvents && ev.period_id != null ? ` (P${ev.period_id})` : ''}
                    </td>
                    {footballEvents ? (
                      <>
                        <td className="live-ev-off">{eventOffense(ev) || '—'}</td>
                        <td className="live-ev-down">{formatDownDistance(ev) || '—'}</td>
                        <td className="live-ev-ball">{formatFieldPosition(ev) || '—'}</td>
                        <td className="live-ev-yds">{formatPlayYards(ev) || '—'}</td>
                        <td className="mk-muted live-ev-score">{formatEventScore(ev) || '—'}</td>
                        <td className="live-events-desc-col" title={eventDescription(ev)}>
                          {eventDescription(ev) || '—'}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="live-events-player">{ev.player_name || '—'}</td>
                        <td>{ev.x != null ? Number(ev.x).toFixed(0) : '—'}</td>
                        <td>{ev.y != null ? Number(ev.y).toFixed(0) : '—'}</td>
                        <td>{ev.outcome != null ? String(ev.outcome) : '—'}</td>
                      </>
                    )}
                    <td className="live-events-tags">{formatBlockTags(ev.block_tags)}</td>
                    <td
                      className="mk-muted live-events-received-col"
                      title={formatReceivedAt(ev.received_at_utc)}
                    >
                      {/* Time only in play: every row is from today, and the date doubled
                          this column's width. Full timestamp stays in the tooltip. */}
                      {footballEvents
                        ? formatTs(ev.received_at_utc)
                        : formatReceivedAt(ev.received_at_utc)}
                    </td>
                    {!footballEvents && (
                      <td className="mk-muted live-events-src-col">{ev.source || '—'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
      <ModalLayer />
    </div>
  );
}
